import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { NetworkStatus, useLazyQuery } from "@apollo/client";

import {
  Device,
  GetDevicesQuery,
  GetDevicesQueryVariables,
  QueryingDevicesByTagQuery,
  QueryingDevicesByTagQueryVariables,
} from "../../../../../API";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { RouteEnum } from "../../../../../common/models/enums";
import {
  GET_DEVICES,
  QUERYING_DEVICES_BY_TAG,
} from "../../../../../common/operations/queries";
import { DEFAULT_LIMIT_COUNT } from "../../../../../common/variables/common";
import { errorNotification } from "../../../../../common/variables/notification";
import {
  useSelectedLocationDropdown,
  useSelectedTagIdDropdown,
} from "../../../../../common/variables/selectedLocationDropdown";

export const useGetDevicesByLocationOrTag = () => {
  const selectedCustomerId = useCustomerIdGuard();
  const selectedTagId = useSelectedTagIdDropdown();
  const locationVariable = useSelectedLocationDropdown();

  const isDevicesPage = window.location.pathname === RouteEnum.Devices;

  // Prevent "no rows" flash on first render:
  // when a location/tag is already selected, we know we will fetch immediately in the effect.
  const [customLoading, setCustomLoading] = useState(
    Boolean(locationVariable?.value || selectedTagId)
  );
  const lastLocationRef = useRef<string | undefined>(undefined);

  const [getDevicesByTag, { data: dataByTag, loading: loadingByTag }] =
    useLazyQuery<QueryingDevicesByTagQuery, QueryingDevicesByTagQueryVariables>(
      QUERYING_DEVICES_BY_TAG
    );

  const [getDevices, { data, fetchMore, networkStatus, loading, refetch }] =
    useLazyQuery<GetDevicesQuery, GetDevicesQueryVariables>(GET_DEVICES);

  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    setCustomLoading(true);

    const fetchInitialDevices = async () => {
      try {
        lastLocationRef.current = locationVariable?.value;

        initialLoadDoneRef.current = false;

        await getDevices({
          variables: {
            limit: DEFAULT_LIMIT_COUNT,
            customerId: selectedCustomerId,
            locationId: locationVariable?.value,
          },
        });

        initialLoadDoneRef.current = true;
      } catch (error: unknown) {
        console.error(`Error while fetching devices: ${error}`);

        errorNotification();
      } finally {
        setCustomLoading(false);
      }
    };

    const fetchInitialDevicesByTag = async () => {
      try {
        await getDevicesByTag({
          variables: {
            tagId: selectedTagId ?? "",
          },
        });
      } catch (error: unknown) {
        console.error(`Error while fetching devices by tag: ${error}`);
      } finally {
        setCustomLoading(false);
      }
    };

    if (selectedTagId) {
      fetchInitialDevicesByTag();
    } else if (selectedCustomerId && !selectedTagId) {
      fetchInitialDevices();
    } else {
      setCustomLoading(false);
    }
  }, [
    selectedCustomerId,
    locationVariable?.value,
    getDevices,
    getDevicesByTag,
    selectedTagId,
  ]);

  const loadMoreDevices = useCallback(() => {
    if (data?.getDevices.nextToken && !loading) {
      fetchMore({
        variables: {
          limit: DEFAULT_LIMIT_COUNT,
          customerId: selectedCustomerId,
          locationId: locationVariable?.value,
          nextToken: data.getDevices.nextToken,
        },
      }).catch(error => {
        const message = String(error?.message ?? error);

        // If pagination token is invalid (e.g., location changed), refresh first page and stop auto-load
        if (message.includes("starting key is invalid")) {
          refetch?.({
            limit: DEFAULT_LIMIT_COUNT,
            customerId: selectedCustomerId,
            locationId: locationVariable?.value,
            nextToken: null,
          }).catch(() => {});

          return;
        }

        console.error(`Error while fetching more devices: ${message}`);

        errorNotification();
      });
    }
  }, [data, fetchMore, selectedCustomerId, locationVariable?.value, loading]);

  // Auto-load more devices when on devices page
  useEffect(() => {
    // Only auto-load when the nextToken corresponds to the same location we fetched for
    const sameLocation = lastLocationRef.current === locationVariable?.value;

    if (
      isDevicesPage &&
      sameLocation &&
      initialLoadDoneRef.current &&
      data?.getDevices?.nextToken &&
      !loading
    ) {
      loadMoreDevices();
    }
  }, [
    isDevicesPage,
    data?.getDevices?.nextToken,
    loading,
    loadMoreDevices,
    locationVariable?.value,
  ]);

  const devices: Device[] = useMemo(
    () => (data?.getDevices?.items as Device[]) || [],
    [data?.getDevices?.items]
  );

  const devicesByTag: Device[] = useMemo(
    () => (dataByTag?.queryingDevicesByTag?.items as Device[]) || [],
    [dataByTag?.queryingDevicesByTag?.items]
  );

  const dataLoading = customLoading || (selectedTagId ? loadingByTag : loading);

  return {
    devices: selectedTagId ? devicesByTag : devices,
    loading: dataLoading,
    refetch,
    fetchMoreLoading: networkStatus === NetworkStatus.fetchMore,
    loadMoreDevices,
    nextToken: data?.getDevices.nextToken,
  } as const;
};
